import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';
import { TabBar } from './registerTabs';
import UtilizationPlanner from './UtilizationPlanner';
import HoursPlanner from './HoursPlanner';
import RevenueGrid from './RevenueGrid';
import MetricToggle from './MetricToggle';
import { usePlannerState } from './plannerState';
import { usePickups, useRateIndex } from './revenueLogic';
import { useRememberedPeriod } from './rememberedPeriod';

const RESOURCES_ENABLED = process.env.REACT_APP_RESOURCES_ENABLED === 'true';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// ---------- Pure helpers (exported for tests — see ResourcePlanning.test.js) ----------

export function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// One row per ResourceAssignment for the Team Allocations grid, with a month -> plannedHours
// lookup for the given year (assignments carry allocations across every year they've ever had).
export function buildAllocationRows(assignments, year) {
  return assignments.map((assignment) => {
    const hoursByMonth = {};
    for (const cell of assignment.monthlyAllocations || []) {
      if (cell.year === year) hoursByMonth[cell.month] = cell.plannedHours;
    }
    return { ...assignment, hoursByMonth };
  });
}

export function allocationRowTotal(row) {
  return round2(Object.values(row.hoursByMonth).reduce((sum, h) => sum + (h || 0), 0));
}

export const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// The current month and everything before it are closed to editing — that time is already reported
// on and, for the current month, part-way through being actualled (Jeremy, 2026-08-21). Admins can
// reopen a month from its column header for the session; nothing is persisted.
export function isMonthClosed(year, month, now = new Date()) {
  return year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth() + 1);
}

// Month-by-month sums for a set of grid rows — used for every roll-up row and the footer total.
export function sumMonths(rows) {
  const hoursByMonth = {};
  for (const month of MONTHS) {
    const sum = rows.reduce((total, row) => total + (row.hoursByMonth[month] || 0), 0);
    if (sum) hoursByMonth[month] = round2(sum);
  }
  return hoursByMonth;
}

// "Manager" is derived, not a role (ADR 0023): anyone who is someone else's managerId.
export function managerIdSet(users) {
  return new Set(users.map((u) => u.managerId).filter(Boolean));
}

export function filterAllocationRows(rows, filters, userById, projectById = {}) {
  return rows.filter((row) => {
    const user = userById[row.userId];
    // An archived project's existing forecast is history, not something to plan against.
    if (projectById[row.projectId]?.projectStatus === 'Archived') return false;
    if (filters.projectId && row.projectId !== filters.projectId) return false;
    if (filters.teamIds?.length && !filters.teamIds.includes(user?.teamId)) return false;
    if (filters.managerId && user?.managerId !== filters.managerId) return false;
    if (filters.userId && row.userId !== filters.userId) return false;
    return true;
  });
}

// Assignments outlive a forecast year: the OnePlan import left ~200 with no 2026 hours at all, which
// rendered as a dozen empty project groups above the seven real ones. Hidden by default, but never a
// row being edited or one just added — those are 0 only because nobody has typed in them yet.
export function hideEmptyAllocationRows(rows, keepIds) {
  return rows.filter((row) => allocationRowTotal(row) !== 0 || keepIds.has(row.id));
}

function compareText(a, b, dir) {
  const cmp = String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
  return dir === 'desc' ? -cmp : cmp;
}

// Project -> Team -> Person, with month sums at every level. The clicked column sorts its own level
// only; the other two stay alphabetical, so sorting Team never scrambles the project order.
// Managers sort ahead of their team regardless of direction — that's a grouping affordance (Jeremy),
// not a value to sort by, so the person sort applies within the manager and non-manager blocks.
// `teamPlans` are TeamMonthlyAllocation rows keyed "projectId::teamId" -> {month: hours}. A future
// year has those and no assignments at all, so the tree is built from the union of both sources --
// grouping only the assignments would render 2027 as an empty grid.
//
// A team row shows its individuals' total once any exist, and its own planned figure until then
// (Jeremy, 2026-08-08). The two are never added: they describe the same work at different
// resolutions, so `planHours` rides alongside for the variance check rather than in the total.
// `extraKeys` ("projectId::teamId") force a group to exist with no hours. Used for baseline
// comparison: a team committed to in the baseline but forecast nowhere has to appear, or the
// shortfall is silently invisible — the single most important variance there is.
export function buildAllocationTree(
  rows,
  { projectById, userById, teamById, managerIds, sort, teamPlans = {}, extraKeys = [] },
) {
  const byProject = new Map();
  const ensureProject = (projectId) => {
    if (!byProject.has(projectId)) byProject.set(projectId, new Map());
    return byProject.get(projectId);
  };
  for (const row of rows) {
    const teams = ensureProject(row.projectId);
    const teamId = userById[row.userId]?.teamId || '';
    if (!teams.has(teamId)) teams.set(teamId, []);
    teams.get(teamId).push(row);
  }
  for (const key of [...Object.keys(teamPlans), ...extraKeys]) {
    const [projectId, teamId] = key.split('::');
    const teams = ensureProject(projectId);
    if (!teams.has(teamId)) teams.set(teamId, []);
  }

  const projects = [...byProject.entries()].map(([projectId, byTeam]) => {
    const teams = [...byTeam.entries()].map(([teamId, teamRows]) => {
      const people = [...teamRows].sort((a, b) => {
        const aMgr = managerIds.has(a.userId);
        const bMgr = managerIds.has(b.userId);
        if (aMgr !== bMgr) return aMgr ? -1 : 1;
        return compareText(
          userById[a.userId]?.name,
          userById[b.userId]?.name,
          sort.key === 'person' ? sort.dir : 'asc',
        );
      });
      const planHours = teamPlans[`${projectId}::${teamId}`] || {};
      const assigned = sumMonths(teamRows);
      const hasPeople = Object.keys(assigned).length > 0;
      return {
        key: `${projectId}::${teamId}`,
        projectId,
        teamId,
        label: teamById[teamId]?.name || 'No team',
        people,
        planHours,
        hasPeople,
        // What this team contributes to its project, and what the grid prints on the team row.
        hoursByMonth: hasPeople ? assigned : planHours,
      };
    });

    teams.sort((a, b) => compareText(a.label, b.label, sort.key === 'team' ? sort.dir : 'asc'));
    return {
      key: projectId,
      projectId,
      label: projectDisplayName(projectById[projectId]) || '—',
      teams,
      hoursByMonth: sumMonths(teams.map((t) => ({ hoursByMonth: t.hoursByMonth }))),
    };
  });

  projects.sort((a, b) => compareText(a.label, b.label, sort.key === 'project' ? sort.dir : 'asc'));
  return projects;
}

// Turns a locked baseline's flat lines back into the same Project → Team → Person shape the live
// grid uses. Lookups are rebuilt from the names captured at lock time, not from current projects and
// people, so a baseline still reads correctly after a rename or a departure — which is the whole
// point of denormalizing them (ADR 0028).
const RND_TEAM_ID = 'rnd';

export function baselineToTree(lines, sort = { key: 'project', dir: 'asc' }) {
  const projectById = {};
  const teamById = {};
  const userById = {};
  const byRow = new Map();
  const teamPlans = {};

  for (const line of lines) {
    // R&D forecast lines (teamId 'rnd') are captured for Revenue Tracker, not a team -- the
    // forecasting grid has no R&D rows, so they'd show here as a fake "R&D" team.
    if (line.teamId === RND_TEAM_ID) continue;
    projectById[line.projectId] = { id: line.projectId, name: line.projectName };
    if (line.teamId) teamById[line.teamId] = { id: line.teamId, name: line.teamName };
    if (line.userId) {
      userById[line.userId] = { id: line.userId, name: line.userName, teamId: line.teamId };
      const key = `${line.projectId}::${line.userId}`;
      if (!byRow.has(key)) {
        // userName rides on the row itself: the read-only grid renders straight from the snapshot
        // rather than looking the person up in today's user list, which may no longer have them.
        byRow.set(key, {
          id: key, projectId: line.projectId, userId: line.userId, userName: line.userName, hoursByMonth: {},
        });
      }
      byRow.get(key).hoursByMonth[line.month] = line.plannedHours;
    } else {
      const key = `${line.projectId}::${line.teamId ?? ''}`;
      if (!teamPlans[key]) teamPlans[key] = {};
      teamPlans[key][line.month] = line.plannedHours;
    }
  }

  const tree = buildAllocationTree([...byRow.values()], {
    projectById, userById, teamById, managerIds: new Set(), sort, teamPlans,
  });

  // Where a team was captured at both levels, the team row shows the COMMITMENT, not the part of it
  // that had reached people — the opposite of the live grid, and the whole reason both are stored.
  // Project totals are recomputed from the team rows so they agree with what is displayed.
  for (const project of tree) {
    for (const team of project.teams) {
      if (Object.keys(team.planHours).length > 0) team.hoursByMonth = team.planHours;
    }
    project.hoursByMonth = sumMonths(project.teams.map((t) => ({ hoursByMonth: t.hoursByMonth })));
  }
  return tree;
}

export function baselineLabel(baseline) {
  return `${baseline.year} ${baseline.period}`;
}

// Two separate numbers, never one. A baseline holds the team-level commitment AND however far it had
// been broken down to people at lock time, and the same work often appears at both levels — adding
// them would double-count. `committed` is the plan: a team's own figure where it has one, the sum of
// its people where it doesn't. `allocated` is how much had reached named people.
export function baselineTotals(lines) {
  const teamPlanned = new Map();
  const peopleByTeam = new Map();
  for (const line of lines) {
    if (line.teamId === RND_TEAM_ID) continue; // see baselineToTree
    const key = `${line.projectId}::${line.teamId ?? ''}`;
    const bucket = line.userId ? peopleByTeam : teamPlanned;
    bucket.set(key, round2((bucket.get(key) || 0) + line.plannedHours));
  }
  let committed = 0;
  for (const hours of teamPlanned.values()) committed += hours;
  for (const [key, hours] of peopleByTeam) if (!teamPlanned.has(key)) committed += hours;
  const allocated = [...peopleByTeam.values()].reduce((s, h) => s + h, 0);
  return { committed: round2(committed), allocated: round2(allocated) };
}

// Committed hours per "projectId::teamId", month by month. A team's own captured figure is the
// commitment where it has one; where the baseline only recorded individuals, their sum is. Never
// both — see ADR 0028.
export function baselineCommittedByTeam(lines) {
  const teamPlanned = new Map();
  const fromPeople = new Map();
  for (const line of lines) {
    const key = `${line.projectId}::${line.teamId ?? ''}`;
    const target = line.userId ? fromPeople : teamPlanned;
    if (!target.has(key)) target.set(key, {});
    const months = target.get(key);
    months[line.month] = round2((months[line.month] || 0) + line.plannedHours);
  }
  for (const [key, months] of fromPeople) if (!teamPlanned.has(key)) teamPlanned.set(key, months);
  return teamPlanned;
}

// Variance is reported at project and team level only (Jeremy, 2026-08-08) — a baseline records what
// a team was committed to, not who would do it, so comparing an individual against it is meaningless.
export function varianceByMonth(actual, baseline) {
  const out = {};
  for (const month of MONTHS) {
    const diff = round2((actual[month] || 0) - (baseline[month] || 0));
    if (diff) out[month] = diff;
  }
  return out;
}

// A team is under- or over-allocated when its people's hours don't add up to what was planned for
// the team. Only meaningful once both exist — before anyone is assigned, the plan IS the forecast.
export function teamPlanVariance(team) {
  if (!team.hasPeople || Object.keys(team.planHours).length === 0) return null;
  const plan = round2(Object.values(team.planHours).reduce((s, h) => s + h, 0));
  const assigned = round2(Object.values(team.hoursByMonth).reduce((s, h) => s + h, 0));
  if (plan === assigned) return null;
  return { plan, assigned, diff: round2(assigned - plan) };
}

// Flattens the tree into what actually renders, honoring which groups are open. Kept separate from
// buildAllocationTree so expanding a group is pure re-render, not a regroup.
export function flattenAllocationTree(tree, expanded) {
  const out = [];
  for (const project of tree) {
    const projectOpen = expanded.has(project.key);
    out.push({ kind: 'project', node: project, open: projectOpen });
    if (!projectOpen) continue;
    for (const team of project.teams) {
      const teamOpen = expanded.has(team.key);
      out.push({ kind: 'team', node: team, open: teamOpen });
      if (!teamOpen) continue;
      for (const row of team.people) out.push({ kind: 'person', node: row });
    }
  }
  return out;
}

// Which projects a new FCST assignment can be added against (Jeremy, 2026-08-04): real projects,
// plus placeholders that haven't been merged into a real project yet -- that's the whole point of a
// placeholder. Once merged, its forecast has already moved onto the real project (see
// ProjectsService.mergePlaceholder), so it drops out here rather than accepting new FCST no one
// would ever see again.
// Archived projects are out too (Jeremy, 2026-08-08): a finished project takes no new forecast, same
// call already made for My Time's picker (TimeEntry.js loggableProjects).
export function forecastableProjects(projects) {
  return projects.filter((p) => !p.mergedIntoProjectId && p.projectStatus !== 'Archived');
}

export function projectDisplayName(project) {
  return project?.isPlaceholder ? `${project.name} (Placeholder)` : project?.name;
}

// Utilization color banding: green under 90%, yellow 90-110%, red over 110%, gray when capacity
// isn't known yet (team has no standardMonthlyHours set).
export function utilizationBadgeClass(pctValue) {
  if (pctValue == null) return 'badge-gray';
  if (pctValue > 110) return 'badge-red';
  if (pctValue >= 90) return 'badge-yellow';
  return 'badge-green';
}

// entryDate is a date-only value the server always serializes as UTC midnight (ADR 0009) -- read
// with UTC getters, not local ones, or this rolls back a day in any timezone behind UTC (the same
// bug class fixed via apiDateKey in TimeEntry.js).
export function formatEntryDate(value) {
  const d = new Date(value);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

// ---------- Approvals grid helpers (duplicated from TimeEntry.js's dateKey/apiDateKey/startOfWeek/
// weekDates rather than imported -- one file per page, same "apps/web can't share code retroactively
// without a decision doc" convention as TASK_TYPE_CATEGORIES elsewhere in this codebase) ----------

export function dateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function apiDateKey(value) {
  const d = new Date(value);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Date-only values come back as UTC midnight; rebuilding the local Date from the UTC parts stops a
// Saturday reading as the previous Friday west of UTC, which would open the queue a week early.
export function approvalWeekDate(value) {
  if (!value) return null;
  const [y, m, d] = apiDateKey(value).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function startOfWeek(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = d.getDay();
  d.setDate(d.getDate() - ((dow + 1) % 7)); // Saturday-start week, matches TimeEntry.js
  return d;
}

// Duplicated from TimeEntry.js along with the helpers above -- TimeEntry.js imports ApprovalsTab from
// this file, so importing back would be a cycle.
const isWeekendDate = (d) => d.getDay() === 0 || d.getDay() === 6;

export function weekDates(anchor) {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

// Administrative time is the one project everyone logs against every week, so it's pinned to the top
// of a person's projects however the grid is sorted (Jeremy, 2026-08-18) -- an approver reads it
// first and it never wanders into the middle of 20-odd customer projects. Keyed off
// `isAdministrative`, not the name, same as TimeEntry.js's projectPickerOptions.
const adminFirst = (a, b) => Number(!!b.isAdministrative) - Number(!!a.isAdministrative);

// One row per (person, project, task type) combo among the entries passed in, day cells keyed the
// same way as My Time's buildMyTimeRows -- the Approvals grid mirrors that grouping/layout per the
// product ask, extended with a person dimension since one queue spans many people.
// `comments` are the TimesheetComments the queue shipped with the entries; a row can carry more than
// one because the row identity here deliberately ignores accountId (Travel for two customers is one
// approval row but two comment rows).
export function buildApprovalRows(entries, users, projects, taskTypes, comments = [], accounts = []) {
  const rows = new Map();
  for (const entry of entries) {
    const key = `${entry.userId}|${entry.projectId}|${entry.taskTypeId}`;
    if (!rows.has(key)) {
      const project = projects.find((p) => p.id === entry.projectId);
      rows.set(key, {
        key,
        userId: entry.userId,
        userName: users.find((u) => u.id === entry.userId)?.name || 'Unknown person',
        projectId: entry.projectId,
        projectName: project?.name || 'Unknown project',
        isAdministrative: !!project?.isAdministrative,
        taskTypeId: entry.taskTypeId,
        taskTypeName: taskTypes.find((t) => t.id === entry.taskTypeId)?.name || 'Unknown task',
        comments: [],
        // A list, not one name: row identity ignores accountId (see above), so a week of Travel for
        // two customers is one approval row covering both.
        accountNames: [],
        entriesByDate: {},
      });
    }
    const row = rows.get(key);
    // A list per day, not one entry: row identity ignores accountId, so the same task type logged on
    // the same day for two customers (Post Go Live for two counties) is two entries in one cell --
    // assigning would drop all but the last, understating the hours and the ids sent to approve.
    const dateKey = apiDateKey(entry.entryDate);
    if (!row.entriesByDate[dateKey]) row.entriesByDate[dateKey] = [];
    row.entriesByDate[dateKey].push(entry);
    if (entry.accountId) {
      const name = accounts.find((a) => a.id === entry.accountId)?.name || 'Unknown customer';
      if (!row.accountNames.includes(name)) row.accountNames.push(name);
    }
  }
  for (const row of rows.values()) row.accountNames.sort((a, b) => a.localeCompare(b));
  for (const c of comments) {
    const row = rows.get(`${c.userId}|${c.projectId}|${c.taskTypeId}`);
    if (row && c.comment && !row.comments.includes(c.comment)) row.comments.push(c.comment);
  }
  // userId breaks a userName tie before anything else: grouping below keys off consecutive runs of
  // userId, so two people who share a display name would otherwise interleave and split one person
  // into two groups (two subtotals, and a duplicate React key on the grid).
  return Array.from(rows.values()).sort(
    (a, b) =>
      a.userName.localeCompare(b.userName) ||
      a.userId.localeCompare(b.userId) ||
      adminFirst(a, b) ||
      a.projectName.localeCompare(b.projectName) ||
      a.taskTypeName.localeCompare(b.taskTypeName),
  );
}

// Collapses buildApprovalRows' flat, pre-sorted list into Person -> Project -> [rows] so the grid
// can merge repeated person/project cells the same way My Time merges repeated project cells.
export function groupApprovalRowsByPerson(rows) {
  const personGroups = [];
  for (const row of rows) {
    let person = personGroups[personGroups.length - 1];
    if (!person || person.userId !== row.userId) {
      person = { userId: row.userId, userName: row.userName, projectGroups: [] };
      personGroups.push(person);
    }
    let project = person.projectGroups[person.projectGroups.length - 1];
    if (!project || project.projectId !== row.projectId) {
      project = {
        projectId: row.projectId,
        projectName: row.projectName,
        isAdministrative: row.isAdministrative,
        rows: [],
      };
      person.projectGroups.push(project);
    }
    project.rows.push(row);
  }
  return personGroups;
}

// Same grouped-grid rule as My Time's sortMyTimeGroups: each key sorts the level it belongs to so
// the rowSpan merges survive. 'person' reorders people, 'project' reorders a person's projects,
// 'task' reorders the rows inside a project. Administrative time stays pinned first either way.
export function sortApprovalGroups(groups, sort) {
  const dir = sort.dir === 'desc' ? -1 : 1;
  if (sort.key === 'project' || sort.key === 'task') {
    return groups.map((person) => ({
      ...person,
      projectGroups: (sort.key === 'task'
        ? person.projectGroups
        : [...person.projectGroups].sort((a, b) => adminFirst(a, b) || dir * a.projectName.localeCompare(b.projectName))
      ).map((pg) =>
        sort.key === 'task'
          ? { ...pg, rows: [...pg.rows].sort((a, b) => dir * a.taskTypeName.localeCompare(b.taskTypeName)) }
          : pg,
      ),
    }));
  }
  return [...groups].sort((a, b) => dir * a.userName.localeCompare(b.userName));
}

export function rowEntryIds(row) {
  return Object.values(row.entriesByDate)
    .flat()
    .map((e) => e.id);
}

// The manager side of Unapprove has one guard the UI needs to know about ahead of the click, not
// after: TimeEntriesService.unapproveAsManager refuses once the PM has also signed off (ADR 0029's
// addendum -- unwinding happens PM-first, same as approving happens manager-first), and a lot of
// historical/imported time was bulk-approved on both sides at once (approve-historical-time.js), so
// this isn't a rare edge case in the Approved tab. Reject has no such restriction (it resets both
// approvals unconditionally), so that's the escape hatch offered here rather than a dead end
// (Jeremy, 2026-08-31).
export function unapproveBlockedReason(row, role, statusTab) {
  if (role !== 'manager' || statusTab !== 'approved') return null;
  const pmApproved = Object.values(row.entriesByDate)
    .flat()
    .some((e) => e.pmApprovedAt);
  return pmApproved
    ? 'The PM has already approved this row -- Unapprove needs the PM first, or use Reject to reset both'
    : null;
}

// One cell can hold several entries (see buildApprovalRows) -- always sum, never read [0].
export function approvalDayHours(row, key) {
  return (row.entriesByDate[key] || []).reduce((sum, e) => sum + (e.hours || 0), 0);
}

export function approvalRowTotal(row, dateKeys) {
  return round2(dateKeys.reduce((sum, key) => sum + approvalDayHours(row, key), 0));
}

export function approvalRowsTotal(rows, dateKeys) {
  return round2(rows.reduce((sum, row) => sum + approvalRowTotal(row, dateKeys), 0));
}

export function approvalDayTotals(rows, dateKeys) {
  return Object.fromEntries(
    dateKeys.map((key) => [key, round2(rows.reduce((sum, row) => sum + approvalDayHours(row, key), 0))]),
  );
}

export function personApprovalRows(person) {
  return person.projectGroups.flatMap((pg) => pg.rows);
}

// ---------- Shared bits ----------

// Same two-bubble glyph as My Time's, duplicated for the same reason as the date helpers above:
// TimeEntry.js imports ApprovalsTab from this file, so importing back would be a cycle.
function CommentIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M2.5 3.5h10a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H7l-3 2.5V12H2.5A1.5 1.5 0 0 1 1 10.5v-5A1.5 1.5 0 0 1 2.5 3.5Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M16 7h1.5A1.5 1.5 0 0 1 19 8.5v5a1.5 1.5 0 0 1-1.5 1.5H16v2l-2.5-2h-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div
      style={{
        padding: '12px 16px', borderRadius: 8, marginBottom: 20,
        background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
        fontSize: 13, color: 'var(--red)',
      }}
    >
      ⚠ {error}
    </div>
  );
}

// Checkbox dropdown (native <details>, so no outside-click plumbing). `value` is an array of ids from
// `teams` ({id, name}); empty means all. Also used for the Capacity month picker.
function TeamFilterSelect({ teams, value, onChange, allLabel = 'All teams', noun = 'teams' }) {
  const label = value.length === 0
    ? allLabel
    : value.length <= 2
      ? teams.filter((t) => value.includes(t.id)).map((t) => t.name).join(', ')
      : `${value.length} ${noun}`;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <details style={{ position: 'relative' }}>
      <summary className="form-select" style={{ cursor: 'pointer', listStyle: 'none', minWidth: 130 }}>{label}</summary>
      <div
        className="card"
        style={{ position: 'absolute', zIndex: 20, marginTop: 4, padding: 8, minWidth: 180, maxHeight: 280, overflowY: 'auto' }}
      >
        {value.length > 0 && (
          <button type="button" className="btn btn-outline" style={{ width: '100%', marginBottom: 6 }} onClick={() => onChange([])}>
            Clear
          </button>
        )}
        {teams.map((team) => (
          <label key={team.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 2px', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={value.includes(team.id)} onChange={() => toggle(team.id)} />
            {team.name}
          </label>
        ))}
      </div>
    </details>
  );
}

// ---------- View: Forecasting (formerly "Team Allocations") ----------

// Click-to-sort header for the three identifying columns. The month columns aren't sortable — they
// are a fixed calendar sequence, same exception as the export-shaped grids elsewhere.
function AllocSortTh({ label, sortKey, sort, onSort, style }) {
  const active = sort.key === sortKey;
  return (
    <th style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => onSort(sortKey)}>
      {label} <span style={{ opacity: active ? 1 : 0.25 }}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

// Who already has a row in this project's grid FOR THIS YEAR. An assignment is project x person and
// carries no year of its own -- only its monthly allocations do -- so someone with 2026 hours and no
// 2027 hours needs offering again in 2027 (Jeremy, 2026-08-08). Excluding them outright left people
// like Catherine on Sedgwick with no way to be given 2027 hours at all: her assignment existed, so
// the dialog refused her, and the grid hid the row because it had no hours yet.
export function usersWithHoursOnProject(assignments, projectId, year) {
  const out = new Set();
  for (const a of assignments) {
    if (a.projectId !== projectId || a.taskId) continue;
    if ((a.monthlyAllocations || []).some((c) => c.year === year && c.plannedHours)) out.add(a.userId);
  }
  return out;
}

// `alreadyShowing` covers people put on screen this session without hours yet -- just added, or
// mid-edit -- who would otherwise be offered a second time.
export function assignablePeople(users, assignments, projectId, teamId, year, alreadyShowing = new Set()) {
  const taken = usersWithHoursOnProject(assignments, projectId, year);
  return users.filter(
    (u) => !taken.has(u.id) && !alreadyShowing.has(u.id) && (!teamId || u.teamId === teamId) && u.isActive !== false,
  );
}

// Project + team + person + which year's hours, because "add a person" on its own doesn't say where
// their hours land (Jeremy, 2026-08-08). Team is explicit rather than inferred from the person: the
// team forecast is what's being drawn down, and it decides which people are even offered.
function AddAssignmentModal({
  projects, users, teams, assignments, year, teamPlans, initial, alreadyShowing, onClose, onSubmit, saving,
}) {
  const [form, setForm] = useState({
    projectId: initial?.projectId || '',
    teamId: initial?.teamId || '',
    userId: '',
    role: '',
    seed: 'blank',
    level: 'person',
  });
  const teamLevel = form.level === 'team';

  const candidates = useMemo(
    () => (form.projectId
      ? assignablePeople(users, assignments, form.projectId, form.teamId, year, alreadyShowing)
      : []),
    [users, assignments, form.projectId, form.teamId, year, alreadyShowing],
  );
  const plan = teamPlans[`${form.projectId}::${form.teamId}`];
  const planTotal = plan ? round2(Object.values(plan).reduce((s, h) => s + h, 0)) : 0;

  // Changing project or team can strand a person who isn't valid for the new pair.
  const update = (patch) => setForm((f) => {
    const next = { ...f, ...patch };
    if (patch.projectId !== undefined || patch.teamId !== undefined) next.userId = '';
    if (!teamPlans[`${next.projectId}::${next.teamId}`]) next.seed = 'blank';
    return next;
  });

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add to the {year} forecast</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...form, plan }); }}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Level</label>
              <select
                className="form-select"
                value={form.level}
                onChange={(e) => setForm((f) => ({ ...f, level: e.target.value, userId: '' }))}
              >
                <option value="person">A person on a project</option>
                <option value="team">A team plan (no people yet)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Project</label>
              <select
                className="form-select"
                value={form.projectId}
                onChange={(e) => update({ projectId: e.target.value })}
              >
                <option value="">Select a project…</option>
                {forecastableProjects(projects).map((p) => (
                  <option key={p.id} value={p.id}>{projectDisplayName(p)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Team</label>
              <select
                className="form-select"
                value={form.teamId}
                onChange={(e) => update({ teamId: e.target.value })}
              >
                {!teamLevel && <option value="">Any team</option>}
                {teamLevel && <option value="">Select a team…</option>}
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            {teamLevel && (
              <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 12 }}>
                Adds a project/team row to the grid. Type the monthly hours there; you can assign people to it later.
              </div>
            )}
            {!teamLevel && (<>
            <div className="form-group">
              <label className="form-label">Person</label>
              <select
                className="form-select"
                value={form.userId}
                disabled={!form.projectId}
                onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
              >
                <option value="">
                  {!form.projectId ? 'Pick a project first…' : 'Select a person…'}
                </option>
                {candidates.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {form.projectId && candidates.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 4 }}>
                  Everyone on this team already has {year} hours on this project.
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Role (optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. BA"
                maxLength={100}
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Starting hours</label>
              <select
                className="form-select"
                value={form.seed}
                disabled={!planTotal}
                onChange={(e) => setForm((f) => ({ ...f, seed: e.target.value }))}
              >
                <option value="blank">Blank — type them in the grid</option>
                {planTotal > 0 && (
                  <option value="plan">Copy the team&apos;s {year} forecast ({planTotal} hrs)</option>
                )}
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 4 }}>
                {planTotal > 0
                  ? 'Copied hours land in the grid unsaved, so you can split them between people before saving.'
                  : 'No team forecast for this project and team in ' + year + '.'}
              </div>
            </div>
            </>)}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !form.projectId || (teamLevel ? !form.teamId : !form.userId)}
            >
              {saving ? 'Adding…' : '+ Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// The FCST grid's equivalent: hours planned per person per project per calendar month, grouped
// Project -> Team -> Person and collapsed to the project level by default (Jeremy, 2026-08-08) --
// the portfolio question ("how much is Charleston forecast for March?") is the common one, and the
// per-person detail is a drill-down. Every dirty row saves in one call (PUT /resource-assignments/
// allocations); roll-ups and footer totals reflect unsaved edits so the numbers move as you type.
// Editing is gated to managers/PMs/admins (ADR 0023) -- read-only for everyone else, since
// GET /resource-assignments stays open.
// Exported only so a harness can render it with mocked apiFetch — the app's own auth is real Entra,
// so this is the one way to eyeball the grid against real data locally.
// Up/down on a number input steps its value by default; in a spreadsheet-style grid people expect
// them to move between cells instead, so we take the keys over and walk the column ourselves.
function gridArrowNav(e) {
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const table = e.target.closest('table');
  if (!table) return;
  e.preventDefault();
  const cells = Array.from(table.querySelectorAll(`input[data-col="${e.target.dataset.col}"]`));
  const next = cells[cells.indexOf(e.target) + (e.key === 'ArrowDown' ? 1 : -1)];
  if (next) { next.focus(); next.select(); }
}

export function TeamAllocationsView({ canEdit, isAdmin, metric = 'hours', onMetric = () => {}, rateIndex = null, pickups = [] }) {
  const rev = metric === 'revenue' && !!rateIndex;
  const [year, setYear] = useRememberedPeriod('resourcePlanning.year', new Date().getFullYear());
  const [filters, setFilters] = useState({ projectId: '', teamIds: [], managerId: '', userId: '' });
  const [sort, setSort] = useState({ key: 'project', dir: 'asc' });
  const [hideEmpty, setHideEmpty] = useState(true);
  const [justAdded, setJustAdded] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamAllocations, setTeamAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [teamDrafts, setTeamDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addContext, setAddContext] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const [locking, setLocking] = useState(false);
  const [baselines, setBaselines] = useState([]);
  const [lockedMessage, setLockedMessage] = useState('');
  const [compareId, setCompareId] = useState('');
  const [compareLines, setCompareLines] = useState(null);
  // "year-month" keys an admin has reopened this session.
  const [reopened, setReopened] = useState(() => new Set());

  const monthLocked = useCallback(
    (month) => isMonthClosed(year, month) && !reopened.has(`${year}-${month}`),
    [year, reopened],
  );
  const toggleMonthLock = (month) => setReopened((prev) => {
    const next = new Set(prev);
    const key = `${year}-${month}`;
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [a, p, u, t, ta, bl] = await Promise.all([
        apiFetch('/resource-assignments'),
        apiFetch('/projects'),
        apiFetch('/users'),
        apiFetch('/teams'),
        apiFetch('/team-allocations'),
        apiFetch('/allocation-baselines'),
      ]);
      setBaselines(bl);
      setAssignments(a);
      setProjects(p);
      setUsers(u);
      setTeams(t);
      setTeamAllocations(ta);
      setDrafts({});
      setTeamDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const teamById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const managerIds = useMemo(() => managerIdSet(users), [users]);
  const managers = useMemo(
    () => users.filter((u) => managerIds.has(u.id)),
    [users, managerIds],
  );

  const rows = useMemo(() => buildAllocationRows(assignments, year), [assignments, year]);

  // Annual is the one people mean by "the baseline"; the quarterly re-locks are the exception you
  // go looking for. Re-picked whenever the year changes, since baselines belong to a year.
  const baselinesForYear = useMemo(() => baselines.filter((b) => b.year === year), [baselines, year]);
  useEffect(() => {
    setCompareId((prev) => {
      if (baselinesForYear.some((b) => b.id === prev)) return prev;
      return (baselinesForYear.find((b) => b.period === 'Annual') || baselinesForYear[0])?.id || '';
    });
  }, [baselinesForYear]);

  useEffect(() => {
    if (!compareId) { setCompareLines(null); return; }
    let cancelled = false;
    apiFetch(`/allocation-baselines/${compareId}`)
      .then((d) => { if (!cancelled) setCompareLines(d.lines); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [compareId]);

  const baselineByTeam = useMemo(
    () => (compareLines ? baselineCommittedByTeam(compareLines) : null),
    [compareLines],
  );

  // Drafts folded in before grouping, so a typed cell moves its team, project, and footer totals
  // immediately rather than only after a save round-trip.
  const effectiveRows = useMemo(() => rows.map((row) => {
    const draft = drafts[row.id];
    if (!draft) return row;
    const hoursByMonth = { ...row.hoursByMonth };
    for (const [month, raw] of Object.entries(draft)) {
      const n = Number(raw);
      if (raw === '' || Number.isNaN(n)) delete hoursByMonth[Number(month)];
      else hoursByMonth[Number(month)] = n;
    }
    return { ...row, hoursByMonth };
  }), [rows, drafts]);

  const matchingRows = useMemo(
    () => filterAllocationRows(effectiveRows, filters, userById, projectById),
    [effectiveRows, filters, userById, projectById],
  );

  const keepIds = useMemo(
    () => new Set([...Object.keys(drafts), ...justAdded]),
    [drafts, justAdded],
  );
  // People on screen only because they were just added or are mid-edit — they have no saved hours
  // yet, so the year check alone would offer them again.
  const shownWithoutHours = useMemo(
    () => new Set(rows.filter((r) => keepIds.has(r.id)).map((r) => r.userId)),
    [rows, keepIds],
  );
  const filteredRows = useMemo(
    () => (hideEmpty ? hideEmptyAllocationRows(matchingRows, keepIds) : matchingRows),
    [matchingRows, hideEmpty, keepIds],
  );
  const hiddenCount = matchingRows.length - filteredRows.length;

  // "projectId::teamId" -> {month: hours} for the shown year, drafts folded in, honoring the same
  // archived/project/team filters the person rows go through.
  const teamPlans = useMemo(() => {
    const out = {};
    for (const cell of teamAllocations) {
      if (cell.year !== year) continue;
      if (projectById[cell.projectId]?.projectStatus === 'Archived') continue;
      if (filters.projectId && cell.projectId !== filters.projectId) continue;
      if (filters.teamIds?.length && !filters.teamIds.includes(cell.teamId)) continue;
      const key = `${cell.projectId}::${cell.teamId}`;
      if (!out[key]) out[key] = {};
      if (cell.plannedHours) out[key][cell.month] = cell.plannedHours;
    }
    for (const [key, draft] of Object.entries(teamDrafts)) {
      const [projectId, teamId] = key.split('::');
      if (projectById[projectId]?.projectStatus === 'Archived') continue;
      if (filters.projectId && projectId !== filters.projectId) continue;
      if (filters.teamIds?.length && !filters.teamIds.includes(teamId)) continue;
      out[key] = { ...out[key] };
      for (const [month, raw] of Object.entries(draft)) {
        const n = Number(raw);
        if (raw === '' || Number.isNaN(n) || n === 0) delete out[key][Number(month)];
        else out[key][Number(month)] = n;
      }
    }
    // A plan of all zeros is the spreadsheet's way of saying "nothing here" -- same treatment as an
    // empty person row, or 2027 would render every project x team combination that has ever existed.
    if (hideEmpty) {
      for (const [key, months] of Object.entries(out)) {
        if (Object.keys(months).length === 0 && !teamDrafts[key]) delete out[key];
      }
    }
    // Filtering to one manager or one person is a question about individuals; a team-level plan has
    // neither, so it drops out rather than appearing to survive the filter.
    if (filters.managerId || filters.userId) return {};
    return out;
  }, [teamAllocations, teamDrafts, year, filters, projectById, hideEmpty]);

  // Groups the baseline commits to but the forecast doesn't mention, so their shortfall is visible.
  // Filtered the same way as everything else, and dropped once a manager/person filter is on, since
  // a team-level commitment can't answer a question about an individual.
  const baselineKeys = useMemo(() => {
    if (!baselineByTeam || filters.managerId || filters.userId) return [];
    return [...baselineByTeam.keys()].filter((key) => {
      const [projectId, teamId] = key.split('::');
      if (projectById[projectId]?.projectStatus === 'Archived') return false;
      if (filters.projectId && projectId !== filters.projectId) return false;
      if (filters.teamIds?.length && !filters.teamIds.includes(teamId)) return false;
      return true;
    });
  }, [baselineByTeam, filters, projectById]);

  const tree = useMemo(
    () => buildAllocationTree(filteredRows, {
      projectById, userById, teamById, managerIds, sort, teamPlans, extraKeys: baselineKeys,
    }),
    [filteredRows, projectById, userById, teamById, managerIds, sort, teamPlans, baselineKeys],
  );

  const visibleRows = useMemo(() => flattenAllocationTree(tree, expanded), [tree, expanded]);
  // Summed from the project rows, not the person rows: a team-level-only project (all of 2027) has
  // no person rows to add up.
  const grandTotals = useMemo(
    () => sumMonths(tree.map((p) => ({ hoursByMonth: p.hoursByMonth }))),
    [tree],
  );
  // Footer variance covers only the teams actually on screen, so it agrees with the rows above it
  // rather than with the whole baseline when a filter is applied.
  const footerBaseline = useMemo(() => {
    if (!baselineByTeam) return null;
    const keys = tree.flatMap((p) => p.teams.map((t) => t.key));
    return sumMonths(keys.map((k) => ({ hoursByMonth: baselineByTeam.get(k) || {} })));
  }, [baselineByTeam, tree]);
  const footerDelta = footerBaseline ? varianceByMonth(grandTotals, footerBaseline) : null;
  const footerDeltaTotal = footerBaseline
    ? round2(Object.values(grandTotals).reduce((s, h) => s + h, 0)
      - Object.values(footerBaseline).reduce((s, h) => s + h, 0))
    : 0;

  const dirtyIds = Object.keys(drafts);
  const dirtyTeamKeys = Object.keys(teamDrafts);
  const dirtyCount = dirtyIds.length + dirtyTeamKeys.length;

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const toggleGroup = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const expandAll = () => setExpanded(new Set(tree.flatMap((p) => [p.key, ...p.teams.map((t) => t.key)])));

  function cellValue(row, month) {
    const draftRow = drafts[row.id];
    if (draftRow && draftRow[month] !== undefined) return draftRow[month];
    return row.hoursByMonth[month] != null ? String(row.hoursByMonth[month]) : '';
  }

  function setCell(rowId, month, value) {
    setDrafts((prev) => ({ ...prev, [rowId]: { ...prev[rowId], [month]: value } }));
  }

  function teamCellValue(team, month) {
    const draft = teamDrafts[team.key];
    if (draft && draft[month] !== undefined) return draft[month];
    return team.planHours[month] != null ? String(team.planHours[month]) : '';
  }

  function setTeamCell(key, month, value) {
    setTeamDrafts((prev) => ({ ...prev, [key]: { ...prev[key], [month]: value } }));
  }

  // Two calls, not one: person hours and team hours live in different tables. Sequential so a
  // failure on the second doesn't leave the UI claiming both saved.
  async function saveAll() {
    setSavingAll(true);
    setError('');
    try {
      if (dirtyIds.length) {
        const payload = dirtyIds.map((rowId) => {
          const row = rows.find((r) => r.id === rowId);
          return {
            assignmentId: rowId,
            cells: MONTHS.map((month) => ({ year, month, plannedHours: Number(cellValue(row, month) || 0) })),
          };
        });
        await apiFetch('/resource-assignments/allocations', {
          method: 'PUT',
          body: JSON.stringify({ rows: payload }),
        });
      }
      if (dirtyTeamKeys.length) {
        const payload = dirtyTeamKeys.map((key) => {
          const [projectId, teamId] = key.split('::');
          const draft = teamDrafts[key];
          return {
            projectId,
            teamId,
            cells: MONTHS.map((month) => ({
              year,
              month,
              plannedHours: Number(draft[month] ?? teamPlans[key]?.[month] ?? 0) || 0,
            })),
          };
        });
        await apiFetch('/team-allocations', { method: 'PUT', body: JSON.stringify({ rows: payload }) });
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAll(false);
    }
  }

  async function lockBaseline(form) {
    setLocking(true);
    setError('');
    try {
      const created = await apiFetch('/allocation-baselines', { method: 'POST', body: JSON.stringify(form) });
      setShowLock(false);
      setLockedMessage(
        `Locked the ${form.year} ${form.period} baseline — ${created?._count?.lines ?? 0} lines captured. See the Baselines tab.`,
      );
      setBaselines(await apiFetch('/allocation-baselines'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLocking(false);
    }
  }

  async function addAssignment(form) {
    if (form.level === 'team') {
      // Nothing to write yet: an empty draft makes the project/team row appear (hide-empty keeps
      // drafted keys), and the first Save writes whatever hours get typed.
      const key = `${form.projectId}::${form.teamId}`;
      setShowAdd(false);
      setAddContext(null);
      setTeamDrafts((prev) => ({ ...prev, [key]: prev[key] || {} }));
      setExpanded((prev) => new Set(prev).add(form.projectId));
      return;
    }
    setAdding(true);
    setError('');
    try {
      // Reuse before creating: the person may already be assigned to this project from another
      // year, since an assignment carries no year of its own. A second task-less assignment for the
      // same project/person is the exact duplicate the OnePlan import left 500 of, and the grid
      // would split their hours across two rows.
      const existing = assignments.find(
        (a) => a.projectId === form.projectId && a.userId === form.userId && !a.taskId,
      );
      const role = form.role.trim();
      let created = existing;
      if (existing) {
        if (role && role !== existing.role) {
          await apiFetch(`/resource-assignments/${existing.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ role }),
          });
        }
      } else {
        created = await apiFetch('/resource-assignments', {
          method: 'POST',
          body: JSON.stringify({ projectId: form.projectId, userId: form.userId, role: role || undefined }),
        });
      }
      // A brand-new assignment has no hours, so the hide-empty filter would swallow it on the
      // reload that follows.
      setShowAdd(false);
      setAddContext(null);
      // Everything below has to follow the reload, not precede it -- load() clears drafts, so a seed
      // applied first would be wiped before it ever reached the grid.
      await load();
      if (created?.id) {
        setJustAdded((prev) => new Set(prev).add(created.id));
        // Seeded as an unsaved draft rather than written straight through: copying a team's whole
        // forecast onto the first person is a starting point to divide up, not an answer.
        if (form.seed === 'plan' && form.plan) {
          // Closed months are skipped — seeding is an edit like any other, and it would otherwise
          // put hours into months the grid won't let you change afterwards.
          setDrafts({
            [created.id]: Object.fromEntries(
              MONTHS.filter((m) => !monthLocked(m))
                .map((m) => [m, form.plan[m] != null ? String(form.plan[m]) : '']),
            ),
          });
        }
        // Open the group the new row landed in, or adding somebody appears to do nothing while the
        // grid sits collapsed at project level.
        const teamId = form.teamId || userById[form.userId]?.teamId || '';
        setExpanded((prev) => new Set(prev).add(form.projectId).add(`${form.projectId}::${teamId}`));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  const colCount = 3 + 12 + 1;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Year</label>
            <input
              type="number"
              className="form-input"
              style={{ width: 90 }}
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || year)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 190 }}>
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={filters.projectId}
              onChange={(e) => setFilters((f) => ({ ...f, projectId: e.target.value }))}
            >
              <option value="">All projects</option>
              {forecastableProjects(projects).map((p) => (
                <option key={p.id} value={p.id}>{projectDisplayName(p)}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 130 }}>
            <label className="form-label">Team</label>
            <TeamFilterSelect teams={teams} value={filters.teamIds} onChange={(v) => setFilters((f) => ({ ...f, teamIds: v }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 150 }}>
            <label className="form-label">Manager</label>
            <select
              className="form-select"
              value={filters.managerId}
              onChange={(e) => setFilters((f) => ({ ...f, managerId: e.target.value }))}
            >
              <option value="">All managers</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 150 }}>
            <label className="form-label">Person</label>
            <select
              className="form-select"
              value={filters.userId}
              onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
            >
              <option value="">All people</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 150 }}>
            <label className="form-label">Baseline Comparison</label>
            <select
              className="form-select"
              value={compareId}
              disabled={baselinesForYear.length === 0}
              onChange={(e) => setCompareId(e.target.value)}
            >
              <option value="">{baselinesForYear.length ? 'No baseline' : `No ${year} baseline`}</option>
              {baselinesForYear.map((b) => (
                <option key={b.id} value={b.id}>{b.period}</option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" aria-hidden="true">&nbsp;</label>
              <button
                className="btn btn-outline"
                disabled={!!dirtyCount}
                title={dirtyCount ? 'Save or discard your changes first — a baseline captures what is saved' : ''}
                onClick={() => setShowLock(true)}
              >
                Lock baseline
              </button>
            </div>
          )}
          <div className="spacer" style={{ flex: 1 }} />
          {/* R&D plans and reports its hours as one monthly total per project, in its own table --
              nothing on this grid, which is why it needs a door rather than a row here. */}
          <Link className="btn btn-outline" to="/rnd-time">R&amp;D hours →</Link>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => { setAddContext(null); setShowAdd(true); }}>+ Add</button>
          )}
        </div>
      </div>

      {!canEdit && (
        <div
          style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: 'var(--light-bg)', border: '1px solid var(--border)',
            fontSize: 13, color: 'var(--text-mid)',
          }}
        >
          Read-only — editing forecasts is limited to managers, project managers, and admins.
        </div>
      )}

      <ErrorBanner error={error} />

      {lockedMessage && (
        <div
          style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: 'rgba(0,150,80,0.08)', border: '1px solid rgba(0,150,80,0.3)', color: 'var(--green)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ flex: 1 }}>✓ {lockedMessage}</span>
          <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setLockedMessage('')}>
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <MetricToggle metric={metric} onChange={onMetric} />
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <button className="btn btn-outline" onClick={expandAll}>Expand all</button>
            <button className="btn btn-outline" onClick={() => setExpanded(new Set())}>Collapse all</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)' }}>
              <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
              Hide rows with no {year} hours
              {hideEmpty && hiddenCount > 0 && <span style={{ opacity: 0.7 }}>({hiddenCount})</span>}
            </label>
            <div className="spacer" style={{ flex: 1 }} />
            {canEdit && (
              <>
                <span style={{ fontSize: 12, color: dirtyCount ? 'var(--heading)' : 'var(--text-mid)' }}>
                  {dirtyCount ? `${dirtyCount} row${dirtyCount === 1 ? '' : 's'} changed` : 'No changes'}
                </span>
                <button
                  className="btn btn-outline"
                  disabled={!dirtyCount || savingAll}
                  onClick={() => { setDrafts({}); setTeamDrafts({}); }}
                >
                  Discard
                </button>
                <button className="btn btn-primary" disabled={!dirtyCount || savingAll} onClick={saveAll}>
                  {savingAll ? 'Saving…' : 'Save changes'}
                </button>
              </>
            )}
          </div>

          {rev ? (
            <RevenueGrid tree={tree} year={year} rateIndex={rateIndex} expanded={expanded} onToggle={toggleGroup} pickups={pickups} />
          ) : (
          <div className="sheet-wrap" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <AllocSortTh label="Project" sortKey="project" sort={sort} onSort={toggleSort} style={{ minWidth: 200 }} />
                  <AllocSortTh label="Team" sortKey="team" sort={sort} onSort={toggleSort} style={{ minWidth: 120 }} />
                  <AllocSortTh label="Person" sortKey="person" sort={sort} onSort={toggleSort} style={{ minWidth: 150 }} />
                  {MONTH_NAMES.map((m, i) => {
                    const month = i + 1;
                    const closed = isMonthClosed(year, month);
                    const locked = monthLocked(month);
                    return (
                      <th key={m} className="cell-month" style={{ textAlign: 'center', width: 56 }}>
                        {m}
                        {closed && (
                          <span
                            role={isAdmin ? 'button' : undefined}
                            tabIndex={isAdmin ? 0 : undefined}
                            aria-label={`${m} ${year} — ${locked ? 'locked' : 'unlocked'}`}
                            title={isAdmin
                              ? `${m} ${year} is closed — click to ${locked ? 'unlock' : 'lock'} it for this session`
                              : `${m} ${year} is closed to edits`}
                            onClick={isAdmin ? () => toggleMonthLock(month) : undefined}
                            onKeyDown={isAdmin
                              ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMonthLock(month); } }
                              : undefined}
                            style={{
                              marginLeft: 3, fontSize: 9, cursor: isAdmin ? 'pointer' : 'default',
                              opacity: locked ? 0.75 : 1,
                            }}
                          >
                            {locked ? '🔒' : '🔓'}
                          </span>
                        )}
                      </th>
                    );
                  })}
                  <th className="cell-month" style={{ textAlign: 'center', width: 72 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((entry, index) => {
                  if (entry.kind !== 'person') {
                    const { node, open } = entry;
                    const isProject = entry.kind === 'project';
                    // A team with nobody assigned yet is where next year's forecast gets typed. Once
                    // its people carry hours the row turns into their total and stops taking input,
                    // so the same number can't be entered at two levels.
                    const teamEditable = !isProject && canEdit && !node.hasPeople;
                    const variance = isProject ? null : teamPlanVariance(node);
                    const dirtyTeam = !isProject && !!teamDrafts[node.key];
                    // Baseline comparison stops at team level: the snapshot records what a team was
                    // committed to, not who would do it.
                    const baseMonths = baselineByTeam
                      ? (isProject
                        ? sumMonths(node.teams.map((t) => ({ hoursByMonth: baselineByTeam.get(t.key) || {} })))
                        : (baselineByTeam.get(node.key) || {}))
                      : null;
                    const delta = baseMonths ? varianceByMonth(node.hoursByMonth, baseMonths) : null;
                    const deltaTotal = baseMonths
                      ? round2(Object.values(node.hoursByMonth).reduce((s, h) => s + h, 0)
                        - Object.values(baseMonths).reduce((s, h) => s + h, 0))
                      : 0;
                    return (
                      <tr
                        key={`${entry.kind}:${node.key}`}
                        className={[isProject ? 'is-group' : 'is-group is-group-sub', dirtyTeam ? 'is-dirty' : ''].filter(Boolean).join(' ')}
                      >
                        <td
                          style={{ cursor: 'pointer', paddingLeft: 10 }}
                          onClick={() => toggleGroup(node.key)}
                        >
                          {isProject ? `${open ? '▾' : '▸'} ${node.label}` : ''}
                        </td>
                        <td style={{ paddingLeft: 26 }}>
                          {/* No caret on a team nobody is assigned to yet — there is nothing under
                              it to open, and 2027 is entirely made of those. */}
                          <span
                            style={{ cursor: isProject || node.hasPeople ? 'pointer' : 'default' }}
                            onClick={() => node.hasPeople && toggleGroup(node.key)}
                          >
                            {isProject ? '' : `${node.hasPeople ? (open ? '▾' : '▸') : '　'} ${node.label}`}
                          </span>
                          {teamEditable && (
                            <span className="badge badge-blue" style={{ marginLeft: 6, fontSize: 9 }}>Team FCST</span>
                          )}
                          {/* Allocating a team's forecast to real people starts here, so the button
                              sits on the row carrying the hours rather than in the page toolbar. */}
                          {!isProject && canEdit && (
                            <button
                              className="btn btn-outline"
                              style={{ marginLeft: 8, fontSize: 10, padding: '2px 7px', fontWeight: 600 }}
                              onClick={() => {
                                setAddContext({ projectId: node.projectId, teamId: node.teamId });
                                setShowAdd(true);
                              }}
                            >
                              + Person
                            </button>
                          )}
                          {variance && (
                            <span
                              className={`badge ${variance.diff > 0 ? 'badge-red' : 'badge-yellow'}`}
                              style={{ marginLeft: 6, fontSize: 9 }}
                              title={`Team plan ${variance.plan} hrs, assigned to people ${variance.assigned} hrs`}
                            >
                              {variance.diff > 0 ? '+' : ''}{variance.diff} vs plan
                            </span>
                          )}
                        </td>
                        <td />
                        {MONTHS.map((month) => {
                          const cellEditable = teamEditable && !monthLocked(month);
                          return (
                          <td key={month} className="cell-num cell-month" style={{ padding: cellEditable ? 0 : '7px 4px' }}>
                            {cellEditable ? (
                              <input
                                type="number"
                                min="0"
                                step="1"
                                data-col={month}
                                value={teamCellValue(node, month)}
                                onKeyDown={gridArrowNav}
                                onChange={(e) => setTeamCell(node.key, month, e.target.value)}
                                style={{
                                  width: '100%', textAlign: 'center', border: '1px solid transparent',
                                  background: 'transparent', font: 'inherit', fontSize: 12, fontWeight: 600,
                                  padding: '7px 2px', borderRadius: 3,
                                }}
                              />
                            ) : (
                              <>
                                {delta?.[month] ? <VarianceTag value={delta[month]} /> : null}
                                {node.hoursByMonth[month] ?? ''}
                              </>
                            )}
                          </td>
                          );
                        })}
                        <td className="cell-num cell-month" style={{ padding: '7px 4px' }}>
                          {deltaTotal ? <VarianceTag value={deltaTotal} /> : null}
                          {round2(Object.values(node.hoursByMonth).reduce((s, h) => s + h, 0)) || ''}
                        </td>
                      </tr>
                    );
                  }

                  const row = entry.node;
                  const user = userById[row.userId];
                  const dirty = !!drafts[row.id];
                  // Banding counts only the leaf rows -- a group row has its own background, and
                  // .is-alt would override it (it comes later in App.css).
                  const leafIndex = visibleRows.slice(0, index).filter((e) => e.kind === 'person').length;
                  const classes = [dirty ? 'is-dirty' : '', leafIndex % 2 === 1 ? 'is-alt' : ''].filter(Boolean).join(' ');
                  return (
                    <tr key={row.id} className={classes}>
                      <td />
                      <td />
                      <td style={{ padding: '8px 10px 8px 42px' }}>
                        {user?.name || '—'}
                        {managerIds.has(row.userId) && (
                          <span className="badge badge-blue" style={{ marginLeft: 6, fontSize: 9 }}>Mgr</span>
                        )}
                        {row.role && <span style={{ color: 'var(--text-mid)', fontSize: 11 }}> · {row.role}</span>}
                      </td>
                      {MONTHS.map((month) => {
                        const locked = monthLocked(month);
                        return (
                          <td key={month} className="cell-num cell-month">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              disabled={!canEdit || locked}
                              title={locked ? `${MONTH_NAMES[month - 1]} ${year} is closed to edits` : undefined}
                              data-col={month}
                              value={cellValue(row, month)}
                              onKeyDown={gridArrowNav}
                              onChange={(e) => setCell(row.id, month, e.target.value)}
                              style={{
                                width: '100%', textAlign: 'center', border: '1px solid transparent',
                                background: 'transparent', font: 'inherit', fontSize: 12, padding: '7px 2px',
                                borderRadius: 3, color: locked ? 'var(--text-mid)' : 'inherit',
                              }}
                            />
                          </td>
                        );
                      })}
                      <td className="cell-num cell-month" style={{ fontWeight: 600 }}>{allocationRowTotal(row)}</td>
                    </tr>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={colCount} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No assignments match these filters{canEdit ? ' — use + Add to create one.' : '.'}
                    </td>
                  </tr>
                )}
              </tbody>
              {visibleRows.length > 0 && (
                <tfoot>
                  <tr className="is-group is-total">
                    <td colSpan={3} style={{ paddingLeft: 10 }}>
                      Total
                      {baselineByTeam && (
                        <span style={{ fontWeight: 400, color: 'var(--text-mid)', fontSize: 11 }}>
                          {' '}vs {baselinesForYear.find((b) => b.id === compareId)?.period} baseline
                        </span>
                      )}
                    </td>
                    {MONTHS.map((month) => (
                      <td key={month} className="cell-num cell-month" style={{ padding: '7px 4px' }}>
                        {footerDelta?.[month] ? <VarianceTag value={footerDelta[month]} /> : null}
                        {grandTotals[month] ?? ''}
                      </td>
                    ))}
                    <td className="cell-num cell-month" style={{ padding: '7px 4px' }}>
                      {footerDeltaTotal ? <VarianceTag value={footerDeltaTotal} /> : null}
                      {round2(Object.values(grandTotals).reduce((s, h) => s + h, 0)) || ''}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          )}
        </>
      )}

      {showLock && (
        <LockBaselineModal
          year={year}
          existing={baselines}
          saving={locking}
          onClose={() => setShowLock(false)}
          onSubmit={lockBaseline}
        />
      )}

      {showAdd && (
        <AddAssignmentModal
          projects={projects}
          users={users}
          teams={teams}
          assignments={assignments}
          year={year}
          teamPlans={teamPlans}
          initial={addContext}
          alreadyShowing={shownWithoutHours}
          saving={adding}
          onClose={() => { setShowAdd(false); setAddContext(null); }}
          onSubmit={addAssignment}
        />
      )}
    </div>
  );
}

// ---------- View: Baselines ----------

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// M01-M12: the monthly forecast Finance compares each close against (FCSTSEP is locked in September).
const BASELINE_PERIODS = ['Annual', 'Q1', 'Q2', 'Q3', 'Q4', ...MONTH_ABBR.map((_, i) => `M${String(i + 1).padStart(2, '0')}`)];
export function baselinePeriodLabel(period) {
  const m = /^M(\d{2})$/.exec(period || '');
  return m ? `${MONTH_ABBR[Number(m[1]) - 1]} monthly (FCST${MONTH_ABBR[Number(m[1]) - 1].toUpperCase()})` : period;
}

// Sits above the forecast figure as context for the number you read below it.
function VarianceTag({ value }) {
  return (
    <div style={{ fontSize: 10, lineHeight: 1.2, fontWeight: 600, color: value < 0 ? 'var(--red)' : 'var(--green)' }}>
      {value > 0 ? '+' : ''}{value}
    </div>
  );
}

function LockBaselineModal({ year, existing, onClose, onSubmit, saving }) {
  const taken = new Set(existing.filter((b) => b.year === year).map((b) => b.period));
  const [form, setForm] = useState({
    // This month's monthly forecast is the usual lock; fall back to the first free period.
    period: [`M${String(new Date().getMonth() + 1).padStart(2, '0')}`, ...BASELINE_PERIODS].find((p) => !taken.has(p)) || 'Annual',
    note: '',
  });
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Lock in the {year} baseline</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ year, ...form }); }}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Period</label>
              <select
                className="form-select"
                value={form.period}
                onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}
              >
                {BASELINE_PERIODS.map((p) => (
                  <option key={p} value={p} disabled={taken.has(p)}>
                    {baselinePeriodLabel(p)}{taken.has(p) ? ' — already locked' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Note (optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. after the Spokane re-plan"
                maxLength={500}
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 12, lineHeight: 1.5 }}>
              Freezes a copy of the {year} forecast exactly as it stands now — team-level rows where
              nobody is assigned yet, individual rows everywhere else. Unsaved changes in the grid
              are not included.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || taken.has(form.period)}>
              {saving ? 'Locking…' : 'Lock in baseline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Read-only by design (Jeremy, 2026-08-08): a baseline that can be edited isn't one.
function BaselinesView({ isAdmin, metric = 'hours', onMetric = () => {}, rateIndex = null, pickups = [] }) {
  const rev = metric === 'revenue' && !!rateIndex;
  const [baselines, setBaselines] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await apiFetch('/allocation-baselines');
      setBaselines(list);
      setSelectedId((prev) => (list.some((b) => b.id === prev) ? prev : list[0]?.id || ''));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    apiFetch(`/allocation-baselines/${selectedId}`)
      .then((d) => { if (!cancelled) { setDetail(d); setExpanded(new Set()); } })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const tree = useMemo(() => (detail ? baselineToTree(detail.lines) : []), [detail]);
  const visibleRows = useMemo(() => flattenAllocationTree(tree, expanded), [tree, expanded]);
  const totals = useMemo(() => sumMonths(tree.map((p) => ({ hoursByMonth: p.hoursByMonth }))), [tree]);

  const toggleGroup = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  async function remove(id) {
    setError('');
    try {
      await apiFetch(`/allocation-baselines/${id}`, { method: 'DELETE' });
      setConfirmingDeleteId('');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return <div className="card">Loading…</div>;

  return (
    <div>
      <ErrorBanner error={error} />
      {baselines.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>
          No baselines locked yet. Use <strong>Lock baseline</strong> on the Forecasting tab to
          freeze a copy of a year&apos;s forecast.
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
                <label className="form-label">Baseline</label>
                <select className="form-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                  {baselines.map((b) => (
                    <option key={b.id} value={b.id}>{baselineLabel(b)}</option>
                  ))}
                </select>
              </div>
              {detail && (
                <div style={{ fontSize: 12, color: 'var(--text-mid)', paddingBottom: 8 }}>
                  Locked {formatEntryDate(detail.lockedAt)}
                  {detail.lockedBy ? ` by ${detail.lockedBy.name}` : ''}
                  {' · '}{baselineTotals(detail.lines).committed} hrs committed
                  {baselineTotals(detail.lines).allocated > 0
                    && `, ${baselineTotals(detail.lines).allocated} of it on named people`}
                  {detail.note ? ` · ${detail.note}` : ''}
                </div>
              )}
              <div className="spacer" style={{ flex: 1 }} />
              {isAdmin && detail && (
                confirmingDeleteId === detail.id ? (
                  <>
                    <button
                      className="btn"
                      style={{ background: 'var(--red)', color: 'var(--white)' }}
                      onClick={() => remove(detail.id)}
                    >
                      Confirm delete
                    </button>
                    <button className="btn btn-outline" onClick={() => setConfirmingDeleteId('')}>Cancel</button>
                  </>
                ) : (
                  <button className="btn btn-outline" onClick={() => setConfirmingDeleteId(detail.id)}>Delete</button>
                )
              )}
            </div>
          </div>

          <MetricToggle metric={metric} onChange={onMetric} />
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <button
              className="btn btn-outline"
              onClick={() => setExpanded(new Set(tree.flatMap((p) => [p.key, ...p.teams.map((t) => t.key)])))}
            >
              Expand all
            </button>
            <button className="btn btn-outline" onClick={() => setExpanded(new Set())}>Collapse all</button>
            <div className="spacer" style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>Read-only — a baseline never changes.</span>
          </div>

          {rev ? (
            <RevenueGrid tree={tree} year={detail?.year ?? new Date().getFullYear()} rateIndex={rateIndex} expanded={expanded} onToggle={toggleGroup} pickups={pickups} />
          ) : (
          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', minWidth: 200 }}>Project</th>
                  <th style={{ textAlign: 'left', minWidth: 120 }}>Team</th>
                  <th style={{ textAlign: 'left', minWidth: 150 }}>Person</th>
                  {MONTH_NAMES.map((m) => (
                    <th key={m} className="cell-month" style={{ textAlign: 'center', width: 56 }}>{m}</th>
                  ))}
                  <th className="cell-month" style={{ textAlign: 'center', width: 72 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((entry, index) => {
                  const isGroup = entry.kind !== 'person';
                  const node = entry.node;
                  const hours = isGroup ? node.hoursByMonth : node.hoursByMonth;
                  const leafIndex = visibleRows.slice(0, index).filter((e) => e.kind === 'person').length;
                  const cls = isGroup
                    ? (entry.kind === 'project' ? 'is-group' : 'is-group is-group-sub')
                    : (leafIndex % 2 === 1 ? 'is-alt' : '');
                  const caret = entry.open ? '▾' : '▸';
                  return (
                    <tr key={`${entry.kind}:${node.key || node.id}`} className={cls}>
                      <td
                        style={{ paddingLeft: 10, cursor: entry.kind === 'project' ? 'pointer' : 'default' }}
                        onClick={() => entry.kind === 'project' && toggleGroup(node.key)}
                      >
                        {entry.kind === 'project' ? `${caret} ${node.label}` : ''}
                      </td>
                      <td
                        style={{ paddingLeft: 26, cursor: entry.kind === 'team' && node.hasPeople ? 'pointer' : 'default' }}
                        onClick={() => entry.kind === 'team' && node.hasPeople && toggleGroup(node.key)}
                      >
                        {entry.kind === 'team' ? `${node.hasPeople ? caret : '　'} ${node.label}` : ''}
                      </td>
                      <td style={{ padding: '8px 10px 8px 42px' }}>
                        {entry.kind === 'person' ? (node.userName || '—') : ''}
                      </td>
                      {MONTHS.map((m) => (
                        <td key={m} className="cell-num cell-month" style={{ padding: '7px 4px' }}>
                          {hours[m] ?? ''}
                        </td>
                      ))}
                      <td className="cell-num cell-month" style={{ padding: '7px 4px', fontWeight: isGroup ? 600 : 400 }}>
                        {round2(Object.values(hours).reduce((s, h) => s + h, 0)) || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="is-group is-total">
                  <td colSpan={3} style={{ paddingLeft: 10 }}>Total</td>
                  {MONTHS.map((m) => (
                    <td key={m} className="cell-num cell-month" style={{ padding: '7px 4px' }}>{totals[m] ?? ''}</td>
                  ))}
                  <td className="cell-num cell-month" style={{ padding: '7px 4px' }}>
                    {round2(Object.values(totals).reduce((s, h) => s + h, 0)) || ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- View: Approvals ----------

// One queue (manager or PM -- ADR 0023's two independently-scoped relationships), rebuilt to mirror
// My Time's layout: rows grouped Person -> Project -> Task Type, columns are the selected week's
// days, with mass select/approve/reject instead of one row at a time (2026-08-05). Manager and PM
// each get their own instance below since the two lists are scoped, filtered, and paged
// independently -- someone can owe both and needs to act on them separately.
function ApprovalQueue({ role, title, subtitle, initialWeekDate, onQueueChanged, users, projects, taskTypes, accounts, onBehalfOfUserId, approverName }) {
  // Opens on the week of the oldest entry still owed (ApprovalsTab resolves it before rendering), so
  // an approver with a backlog doesn't land on an empty current week and have to page back to it.
  const [anchor, setAnchor] = useState(() => initialWeekDate || new Date());
  const [statusTab, setStatusTab] = useState('pending'); // 'pending' | 'approved' -- Approved feeds Unapprove
  const [personFilter, setPersonFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [entries, setEntries] = useState([]);
  const [comments, setComments] = useState([]);
  const [openCommentKey, setOpenCommentKey] = useState(''); // one row's note open at a time
  const [sort, setSort] = useState({ key: 'person', dir: 'asc' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const days = useMemo(() => weekDates(anchor), [anchor]);
  const keys = useMemo(() => days.map(dateKey), [days]);
  const weekendKeys = useMemo(() => new Set(days.filter(isWeekendDate).map(dateKey)), [days]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch(
        `/time-entries/pending-approvals?from=${keys[0]}&to=${keys[6]}&status=${statusTab}` +
          (onBehalfOfUserId ? `&onBehalfOfUserId=${onBehalfOfUserId}` : ''),
      );
      setEntries(role === 'manager' ? result.asManager : result.asPm);
      setComments(result.comments || []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, statusTab, onBehalfOfUserId, keys[0], keys[6]]);

  useEffect(() => { load(); }, [load]);

  // Filter dropdown options come from the full (week-scoped, unfiltered) queue, not the filtered
  // result -- otherwise picking a person would shrink the project dropdown to just their projects
  // with no way back to "everyone."
  const filterablePeople = useMemo(() => {
    const ids = new Set(entries.map((e) => e.userId));
    return users.filter((u) => ids.has(u.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries, users]);
  const filterableProjects = useMemo(() => {
    const ids = new Set(entries.map((e) => e.projectId));
    return projects.filter((p) => ids.has(p.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries, projects]);

  const filteredEntries = useMemo(
    () =>
      entries.filter(
        (e) => (!personFilter || e.userId === personFilter) && (!projectFilter || e.projectId === projectFilter),
      ),
    [entries, personFilter, projectFilter],
  );
  const rows = useMemo(
    () => buildApprovalRows(filteredEntries, users, projects, taskTypes, comments, accounts),
    [filteredEntries, users, projects, taskTypes, comments, accounts],
  );
  const groups = useMemo(() => sortApprovalGroups(groupApprovalRowsByPerson(rows), sort), [rows, sort]);
  // Unfiltered -- Reject has no ordering restriction and can act on every selected row even when
  // Unapprove can't (see blockedUnapproveIds below), so Select All shouldn't pre-exclude anyone.
  const allRowIds = useMemo(() => rows.flatMap(rowEntryIds), [rows]);
  const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedIds.has(id));
  // Entry ids Unapprove will refuse (PM already approved -- ADR 0029's addendum). Computed once per
  // render rather than per row so the toolbar button can tell the user before the click fails.
  const blockedUnapproveIds = useMemo(
    () => new Set(rows.filter((r) => unapproveBlockedReason(r, role, statusTab)).flatMap(rowEntryIds)),
    [rows, role, statusTab],
  );
  const queueDayTotals = useMemo(() => approvalDayTotals(rows, keys), [rows, keys]);

  const toggleSort = useCallback((key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }, []);

  function toggleRow(row) {
    const ids = rowEntryIds(row);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const rowFullySelected = ids.every((id) => next.has(id));
      ids.forEach((id) => (rowFullySelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds(allSelected ? new Set() : new Set(allRowIds));
  }

  async function runBatch(action, extra, idsOverride) {
    const ids = idsOverride || Array.from(selectedIds);
    setBusy(true);
    setError('');
    try {
      const result = await apiFetch(`/time-entries/${action}-${role}-batch`, {
        method: 'POST',
        body: JSON.stringify({
          ids,
          ...(onBehalfOfUserId ? { onBehalfOfUserId } : {}),
          ...extra,
        }),
      });
      if (result.failed?.length) {
        setError(`${result.failed.length} of ${ids.length} could not be updated: ${result.failed[0].reason}`);
      }
      setRejecting(false);
      setRejectReason('');
      await load();
      onQueueChanged?.(); // keeps My Time's Approvals badge from outliving the work it counted
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title">{title}</div>
      {subtitle && (
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 12 }}>{subtitle}</div>
      )}

      <div style={{ display: 'flex', gap: 0, marginBottom: 12, width: 'fit-content', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {[{ key: 'pending', label: 'Pending' }, { key: 'approved', label: 'Approved' }].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusTab(tab.key)}
            style={{
              fontSize: 12, fontWeight: 600, padding: '6px 14px', border: 'none', cursor: 'pointer',
              background: statusTab === tab.key ? 'var(--primary-dark)' : 'var(--surface)',
              color: statusTab === tab.key ? 'var(--white)' : 'var(--text-mid)',
            }}
            aria-pressed={statusTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}>
          ← Prev week
        </button>
        <div style={{ fontWeight: 600, fontSize: 13, minWidth: 170, textAlign: 'center' }}>
          {days[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          {' – '}
          {days[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}>
          Next week →
        </button>
        <select className="form-select" style={{ fontSize: 12, padding: '6px 10px', width: 180 }} value={personFilter} onChange={(e) => setPersonFilter(e.target.value)}>
          <option value="">All people</option>
          {filterablePeople.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select className="form-select" style={{ fontSize: 12, padding: '6px 10px', width: 200 }} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="">All projects</option>
          {filterableProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <ErrorBanner error={error} />

      {loading ? (
        <div style={{ padding: '8px 0', color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '8px 0', color: 'var(--text-mid)', fontSize: 13 }}>
          {entries.length > 0
            ? 'No entries match the selected filters.'
            : statusTab === 'approved'
              ? `Nothing approved by ${approverName || 'you'} as ${role === 'manager' ? 'a manager' : 'a PM'} this week.`
              : `Nothing pending ${approverName ? `${approverName}'s` : 'your'} approval as ${role === 'manager' ? 'a manager' : 'a PM'} this week.`}
        </div>
      ) : (
        <>
          {selectedIds.size > 0 && (() => {
            // Reject resets both approvals unconditionally (no ordering restriction like Unapprove
            // has), so it's offered on the Approved tab too -- the way out for a row Unapprove can't
            // touch because the PM has already signed off (unapproveBlockedReason).
            const eligibleUnapproveIds = Array.from(selectedIds).filter((id) => !blockedUnapproveIds.has(id));
            const allSelectedBlocked = eligibleUnapproveIds.length === 0;
            return (
            <div className="mass-action-toolbar">
              <span>{selectedIds.size} selected</span>
              {statusTab === 'approved' && (
                <button
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '4px 12px' }}
                  disabled={busy || allSelectedBlocked}
                  title={allSelectedBlocked ? 'The PM has already approved every row selected -- use Reject instead' : undefined}
                  onClick={() => runBatch('unapprove', undefined, eligibleUnapproveIds)}
                >
                  {busy && !rejecting
                    ? 'Unapproving…'
                    : eligibleUnapproveIds.length < selectedIds.size
                      ? `Unapprove selected (${eligibleUnapproveIds.length} of ${selectedIds.size})`
                      : 'Unapprove selected'}
                </button>
              )}
              {statusTab !== 'approved' && (
                <button className="btn btn-success" style={{ fontSize: 11, padding: '4px 12px' }} disabled={busy} onClick={() => runBatch('approve')}>
                  {busy && !rejecting ? 'Approving…' : 'Approve selected'}
                </button>
              )}
              {rejecting ? (
                <>
                  <input
                    type="text"
                    className="form-input"
                    style={{ fontSize: 12, padding: '5px 8px', width: 240 }}
                    placeholder="Reason (required)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  <button
                    className="btn"
                    style={{ background: 'var(--red)', color: 'var(--white)', fontSize: 11, padding: '4px 12px' }}
                    disabled={busy || !rejectReason.trim()}
                    onClick={() => runBatch('reject', { reason: rejectReason.trim() })}
                  >
                    {busy ? 'Rejecting…' : 'Confirm reject'}
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 12px' }} onClick={() => { setRejecting(false); setRejectReason(''); }}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 12px' }} disabled={busy} onClick={() => setRejecting(true)}>
                  Reject selected…
                </button>
              )}
            </div>
            );
          })()}

          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  <th className="cell-check">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select every visible row" />
                  </th>
                  <AllocSortTh label="Person" sortKey="person" sort={sort} onSort={toggleSort} style={{ minWidth: 140 }} />
                  <AllocSortTh label="Project" sortKey="project" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
                  <AllocSortTh label="Task" sortKey="task" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
                  <th style={{ textAlign: 'center', width: 52 }} title="Read the note the person left on this row">
                    Notes
                  </th>
                  {days.map((d, i) => (
                    <th
                      key={keys[i]}
                      className={isWeekendDate(d) ? 'is-weekend' : undefined}
                      style={{ textAlign: 'center', width: 56, borderLeft: '1px solid var(--border)' }}
                    >
                      {d.toLocaleDateString(undefined, { weekday: 'short' })}
                      <br />
                      <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text-mid)' }}>{d.getDate()}</span>
                    </th>
                  ))}
                  <th style={{ textAlign: 'center', width: 60, borderLeft: '1px solid var(--border)' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((person) => {
                  const personRows = personApprovalRows(person);
                  const personDayTotals = approvalDayTotals(personRows, keys);
                  // An open note is its own <tr>, so both merged cells have to span it too.
                  const openInPerson = personRows.filter((r) => r.key === openCommentKey).length;
                  let personRowIndex = 0;
                  return (
                    <React.Fragment key={person.userId}>
                      {person.projectGroups.map((projectGroup) => {
                        const openInGroup = projectGroup.rows.filter((r) => r.key === openCommentKey).length;
                        return projectGroup.rows.map((row, i) => {
                          const ids = rowEntryIds(row);
                          const blockedReason = unapproveBlockedReason(row, role, statusTab);
                          const isFirstOfPerson = personRowIndex === 0;
                          const commentOpen = openCommentKey === row.key;
                          personRowIndex += 1;
                          return (
                            <React.Fragment key={row.key}>
                            <tr>
                              <td className="cell-check">
                                {/* Not disabled even when blockedReason is set -- Reject has no
                                    ordering restriction and can still act on this row, unlike
                                    Unapprove (see blockedUnapproveIds). */}
                                <input
                                  type="checkbox"
                                  checked={ids.length > 0 && ids.every((id) => selectedIds.has(id))}
                                  onChange={() => toggleRow(row)}
                                />
                              </td>
                              {isFirstOfPerson && (
                                <td rowSpan={personRows.length + openInPerson} style={{ padding: '8px 10px', fontWeight: 700, verticalAlign: 'top' }}>
                                  {person.userName}
                                </td>
                              )}
                              {i === 0 && (
                                <td rowSpan={projectGroup.rows.length + openInGroup} style={{ padding: '8px 10px', fontWeight: 600, verticalAlign: 'top' }}>
                                  {projectGroup.projectName}
                                </td>
                              )}
                              <td style={{ padding: '8px 10px', color: 'var(--text-mid)' }}>
                                {row.taskTypeName}
                                {/* Only account-based task types carry one (Travel, Post Go Live
                                    Support) -- for everything else the project names the customer. */}
                                {row.accountNames?.length > 0 && (
                                  <div style={{ fontSize: 11, marginTop: 2, color: 'var(--text-light)' }}>
                                    {row.accountNames.join(', ')}
                                  </div>
                                )}
                                {blockedReason && (
                                  <div style={{ fontSize: 11, marginTop: 2, color: 'var(--red)', fontStyle: 'italic' }}>
                                    {blockedReason}
                                  </div>
                                )}
                              </td>
                              {/* Read-only: the note is part of what is being signed off, and the approver
                                  has no other way to see it. Editing it is My Time's job. */}
                              <td style={{ textAlign: 'center' }}>
                                {row.comments.length > 0 && (
                                  <button
                                    title={commentOpen ? 'Hide the note' : row.comments.join(' / ')}
                                    aria-label="Show the note left on this row"
                                    aria-expanded={commentOpen}
                                    onClick={() => setOpenCommentKey(commentOpen ? '' : row.key)}
                                    style={{
                                      border: 'none', background: 'transparent', cursor: 'pointer', padding: 2,
                                      margin: '0 auto', display: 'block', color: 'var(--text-mid)',
                                    }}
                                  >
                                    <CommentIcon />
                                  </button>
                                )}
                              </td>
                              {keys.map((key) => {
                                const dayEntries = row.entriesByDate[key];
                                return (
                                  <td
                                    key={key}
                                    className={weekendKeys.has(key) ? 'cell-num is-weekend' : 'cell-num'}
                                    style={{ borderLeft: '1px solid var(--border)' }}
                                  >
                                    {dayEntries?.length ? round2(approvalDayHours(row, key)) : ''}
                                  </td>
                                );
                              })}
                              <td className="cell-num" style={{ fontWeight: 600, borderLeft: '1px solid var(--border)' }}>
                                {approvalRowTotal(row, keys)}
                              </td>
                            </tr>
                            {commentOpen && (
                              <tr>
                                <td />
                                <td colSpan={10} style={{ padding: '0 10px 10px' }}>
                                  {row.comments.map((c) => (
                                    <div key={c} style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--text-mid)' }}>
                                      {c}
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        });
                      })}
                      <tr className="is-group">
                        <td colSpan={5}>{person.userName} total</td>
                        {keys.map((key) => (
                          <td
                            key={key}
                            className={weekendKeys.has(key) ? 'cell-num is-weekend' : 'cell-num'}
                            style={{ borderLeft: '1px solid var(--border)' }}
                          >
                            {personDayTotals[key] || ''}
                          </td>
                        ))}
                        <td className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>
                          {approvalRowsTotal(personRows, keys)}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {groups.length > 1 && (
                  <tr className="is-group">
                    <td colSpan={5}>All people</td>
                    {keys.map((key) => (
                      <td
                        key={key}
                        className={weekendKeys.has(key) ? 'cell-num is-weekend' : 'cell-num'}
                        style={{ borderLeft: '1px solid var(--border)' }}
                      >
                        {queueDayTotals[key] || ''}
                      </td>
                    ))}
                    <td className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>
                      {approvalRowsTotal(rows, keys)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ManagerApprovalsSection({ initialWeekDate, onQueueChanged, users, projects, taskTypes, accounts, onBehalfOfUserId, approverName }) {
  return (
    <ApprovalQueue
      role="manager"
      title={approverName ? `Awaiting ${approverName}'s approval as manager` : 'Awaiting your approval as manager'}
      initialWeekDate={initialWeekDate}
      onQueueChanged={onQueueChanged}
      users={users}
      projects={projects}
      taskTypes={taskTypes}
      accounts={accounts}
      onBehalfOfUserId={onBehalfOfUserId}
      approverName={approverName}
    />
  );
}

function ProjectApprovalsSection({ initialWeekDate, onQueueChanged, users, projects, taskTypes, accounts, onBehalfOfUserId, approverName }) {
  return (
    <ApprovalQueue
      role="pm"
      title={
        approverName
          ? `Awaiting ${approverName}'s approval as project manager`
          : 'Awaiting your approval as project manager'
      }
      subtitle="Time reaches the PM after the resource's manager has approved it (Submit → Manager → PM)."
      initialWeekDate={initialWeekDate}
      onQueueChanged={onQueueChanged}
      users={users}
      projects={projects}
      taskTypes={taskTypes}
      accounts={accounts}
      onBehalfOfUserId={onBehalfOfUserId}
      approverName={approverName}
    />
  );
}

// Rendered by My Time (TimeEntry.js) -- approvals live next to your own timesheet, not in Resource
// Planning.
// `onBehalfOfUserId`/`approver` come from My Time's person switcher (2026-08-25): an admin or anyone
// up the reporting chain can work that person's queue. Sections are then gated on THEIR manager/PM
// standing, not the signed-in user's, so the tab shows the queues the delegate actually has.
export function ApprovalsTab({ permissions, isAdmin, onQueueChanged, onBehalfOfUserId, approver }) {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  // Only used to name the customer on an account-based row (Travel, Post Go Live Support).
  const [accounts, setAccounts] = useState([]);
  // Resolved before the queues mount so each can open on its own oldest outstanding week without a
  // second fetch-then-jump round trip. Falls back to the current week when nothing is outstanding.
  // `forUserId` tags whose backlog this is: the queues below are keyed on it and stay unmounted
  // until the switched-to approver's landing week has arrived, so they can't open on the previous
  // person's week and then jump.
  const [earliest, setEarliest] = useState({ forUserId: null, asManager: null, asPm: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch('/users'),
      apiFetch('/projects'),
      // Inactive included: this list is only ever read to label an approval row, and a row logged
      // against a retired type should show its real name, not "Unknown task" (Jeremy, 2026-08-13).
      apiFetch('/task-types?includeInactive=true'),
      apiFetch('/accounts').catch(() => []),
    ])
      .then(([u, p, t, a]) => {
        setUsers(u); setProjects(p); setTaskTypes(t); setAccounts(a);
      })
      .finally(() => setLoading(false));
  }, []);

  // Separate from the reference data above so switching approver re-resolves the landing week
  // without re-fetching users/projects/task types.
  const approverKey = onBehalfOfUserId || 'self';
  useEffect(() => {
    apiFetch(`/time-entries/earliest-pending-approval${onBehalfOfUserId ? `?onBehalfOfUserId=${onBehalfOfUserId}` : ''}`)
      .then((e) => setEarliest({ ...e, forUserId: onBehalfOfUserId || 'self' }))
      .catch(() => setEarliest({ forUserId: onBehalfOfUserId || 'self', asManager: null, asPm: null }));
  }, [onBehalfOfUserId]);

  if (loading || earliest.forUserId !== approverKey) return <div className="card">Loading…</div>;

  const approverName = onBehalfOfUserId ? approver?.name || 'this person' : '';
  const showManager = onBehalfOfUserId ? !!approver?.isManagerOfAnyone : isAdmin || permissions?.isManagerOfAnyone;
  const showPm = onBehalfOfUserId
    ? !!approver?.isProjectManagerOfAny
    : isAdmin || permissions?.isProjectManagerOfAny;

  return (
    <div>
      {onBehalfOfUserId && (
        <div className="proxy-banner" style={{ marginBottom: 16 }}>
          You are approving as <strong>{approverName}</strong> — their queue, their reports and
          projects. The sign-off is still recorded against you.
        </div>
      )}
      {/* Someone in the chain who neither manages nor PMs has no queue to stand in for. */}
      {onBehalfOfUserId && !showManager && !showPm && (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>
          {approverName} isn’t a line manager or a project manager, so there’s nothing to approve as
          them.
        </div>
      )}
      {/* Keys are per role AND approver: two siblings sharing one key left phantom copies of the
          manager queue mounted. The remount on switch is what re-opens each queue on the newly
          selected person's oldest outstanding week. */}
      {showManager && (
        <ManagerApprovalsSection
          key={`manager-${approverKey}`}
          initialWeekDate={approvalWeekDate(earliest.asManager)}
          onQueueChanged={onQueueChanged}
          users={users}
          projects={projects}
          taskTypes={taskTypes}
          accounts={accounts}
          onBehalfOfUserId={onBehalfOfUserId}
          approverName={approverName}
        />
      )}
      {showPm && (
        <ProjectApprovalsSection
          key={`pm-${approverKey}`}
          initialWeekDate={approvalWeekDate(earliest.asPm)}
          onQueueChanged={onQueueChanged}
          users={users}
          projects={projects}
          taskTypes={taskTypes}
          accounts={accounts}
          onBehalfOfUserId={onBehalfOfUserId}
          approverName={approverName}
        />
      )}
    </div>
  );
}

// ---------- Top level ----------

const VIEWS = [
  { key: 'forecasting', label: 'Forecasting' },
  { key: 'baselines', label: 'Baselines' },
  { key: 'hours', label: 'Capacity' },
  { key: 'utilization', label: 'Utilization' },
];

function ResourcePlanning() {
  // permissions comes from the shared context (loaded once at sign-in) rather than a fetch here --
  // the nav needs the same answer to decide whether to offer this page at all.
  const { currentUser, permissions } = useApp();
  const [view, setView] = useState('forecasting');
  // Hours/Revenue and the Capacity/Utilization filters live here so they survive switching tabs.
  const [metric, setMetric] = useState('hours');
  const rateIndex = useRateIndex(RESOURCES_ENABLED && metric === 'revenue');
  const pickups = usePickups(RESOURCES_ENABLED && metric === 'revenue') || [];
  const planner = usePlannerState();

  if (!RESOURCES_ENABLED) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">📊</div>
        <div className="placeholder-title">Resource Planning</div>
        <div className="placeholder-sub">
          Built, but not switched on in this environment. Set{' '}
          <code>REACT_APP_RESOURCES_ENABLED=true</code> to enable it.
        </div>
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';
  const views = VIEWS;
  const activeView = view;

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Resourcing</div>
        <h1 className="page-title">Resource Planning</h1>
        <p className="page-subtitle">Forecasting, capacity and utilization.</p>
      </div>

      {/* Not `merge`: each view is its own component/cards below, not one panel to attach to. */}
      <TabBar tabs={views} active={activeView} onSelect={setView} />

      {activeView === 'forecasting' ? (
        <TeamAllocationsView canEdit={!!permissions?.canEditForecasts} isAdmin={isAdmin} metric={metric} onMetric={setMetric} rateIndex={rateIndex} pickups={pickups} />
      ) : activeView === 'baselines' ? (
        <BaselinesView isAdmin={isAdmin} metric={metric} onMetric={setMetric} rateIndex={rateIndex} pickups={pickups} />
      ) : activeView === 'hours' ? (
        <HoursPlanner canEdit={!!permissions?.canEditForecasts} shared={planner} metric={metric} onMetric={setMetric} rateIndex={rateIndex} />
      ) : activeView === 'utilization' ? (
        <UtilizationPlanner shared={planner} />
      ) : null}
    </div>
  );
}

export default ResourcePlanning;
